import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

export interface UserProfile {
  id: number;
  email: string;
  name?: string;
  prenom?: string;
  telephone?: string;
  adresse?: string;
  complement?: string;
  code_postal?: string;
  ville?: string;
}

interface AuthContextType {
  user: UserProfile | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  loginWithGoogle: (credential: string) => Promise<{ ok: boolean; error?: string }>;
  register: (email: string, password: string, name: string) => Promise<{ ok: boolean; error?: string }>;
  logout: () => Promise<void>;
  updateProfile: (data: Partial<UserProfile>) => Promise<{ ok: boolean; error?: string }>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const res = await fetch("/backend/api/me.php", { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshUser(); }, [refreshUser]);

  const login = async (email: string, password: string) => {
    try {
      const res = await fetch("/backend/api/login.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (res.ok) {
        await refreshUser();
        return { ok: true };
      }
      return { ok: false, error: data.error || "Identifiants incorrects." };
    } catch {
      return { ok: false, error: "Erreur réseau." };
    }
  };

  const loginWithGoogle = async (credential: string) => {
    try {
      const res = await fetch("/backend/api/google-login.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ credential }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await refreshUser();
        return { ok: true };
      }
      return { ok: false, error: data.error || "Connexion Google échouée." };
    } catch {
      return { ok: false, error: "Erreur réseau." };
    }
  };

  const register = async (email: string, password: string, name: string) => {
    try {
      const res = await fetch("/backend/api/register.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password, name }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await refreshUser();
        return { ok: true };
      }
      return { ok: false, error: data.error || "Erreur lors de la création du compte." };
    } catch {
      return { ok: false, error: "Erreur réseau." };
    }
  };

  const logout = async () => {
    await fetch("/backend/api/logout.php", { method: "POST", credentials: "include" });
    setUser(null);
  };

  const updateProfile = async (profileData: Partial<UserProfile>) => {
    try {
      const res = await fetch("/backend/api/update-profile.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(profileData),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await refreshUser();
        return { ok: true };
      }
      return { ok: false, error: data.error || "Erreur lors de la mise à jour." };
    } catch {
      return { ok: false, error: "Erreur réseau." };
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, register, logout, updateProfile, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
